// useRiskWatch — the shell-wide view of the account kill switch.
//
// Two jobs the Portfolio page used to do alone (audit A §1.3):
//   1. Know whether trading is HALTED, so the header can show it on every page
//      (GET /api/risk → `halted.{user,global,reason,since,degraded}`), re-read on
//      window focus, every 60 s, and after every `risk:blocked`.
//   2. Surface `risk:blocked` ("Order blocked: …") on every page, not only
//      while RiskControls happens to be mounted.
//
// The socket contract is RiskControls.js's, byte for byte: connect with
// `auth: { token }`, emit a bare `join` on connect (the server derives the
// user + bot rooms from the handshake token), listen for `risk:blocked`. The
// API emits each block to BOTH `bot:<id>` and `user:<uid>`, so events are
// de-duplicated on (botId | code | at), bounded at 500 keys.
//
// While RiskControls is on screen it toasts the same event itself, so the
// shell stays quiet on the routes that mount it (`suppressToastOn`), and only
// refreshes its halt state there.
//
// The read is the shared cached query (ui/data useRiskQuery, key ['risk']):
// this hook is its ONE poller (60 s, paused while the tab is hidden, plus
// window focus), and AutoTrader / Portfolio read the same entry instead of
// fetching and polling /api/risk themselves. `polling: true` tells them so.
import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useRiskQuery } from '../data/queries';
import { toast } from '../components/Toast';

export const EMPTY_RISK = { loaded: false, halted: false, global: false, user: false, degraded: false, reason: null, since: null, blockExits: false };

export function readHalt(res) {
  const h = (res && res.halted) || {};
  return {
    loaded: true,
    halted: Boolean(h.global || h.user),
    global: Boolean(h.global),
    user: Boolean(h.user),
    degraded: Boolean(h.degraded),
    reason: h.reason || null,
    since: h.since || null,
    blockExits: Boolean(h.blockExits),
  };
}

export function blockKey(ev) {
  return `${ev.botId ?? ev.bot_id ?? '?'}|${ev.code || '?'}|${ev.at || ''}`;
}

export default function useRiskWatch({ enabled = true, suppressToast = false } = {}) {
  const hasToken = Boolean(localStorage.getItem('token'));
  const q = useRiskQuery({ enabled: enabled && hasToken, poll: true });
  // 404 = backend predates the kill switch; anything else = unknown. Either
  // way the header shows nothing rather than a guess (a failed RE-read keeps
  // the last good halt state).
  const risk = q.data !== undefined ? readHalt(q.data) : q.isError ? { ...EMPTY_RISK, loaded: true } : EMPTY_RISK;
  const suppressRef = useRef(suppressToast);
  suppressRef.current = suppressToast;
  const seenRef = useRef(new Set());
  // The socket, shared through ShellContext so pages (Portfolio's bot feed)
  // attach to it instead of opening a second connection.
  const [socket, setSocket] = useState(null);
  // Bumped on every (de-duplicated) risk:blocked, so a page can re-read its
  // own risk panel without listening itself.
  const [blocks, setBlocks] = useState(0);

  const { refetch } = q;
  const refresh = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    await refetch();
  }, [refetch]);

  // react-query refetches a stale query on `visibilitychange`; the old poll
  // also re-read on window `focus` (switching back from another window).
  useEffect(() => {
    if (!enabled) return undefined;
    // Joins a read already in flight (a tab switch fires both events).
    const onFocus = () => { if (localStorage.getItem('token')) refetch({ cancelRefetch: false }); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [enabled, refetch]);

  useEffect(() => {
    if (!enabled) return undefined;
    const token = localStorage.getItem('token');
    if (!token) return undefined;
    const url = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3005';
    let socket;
    try {
      socket = io(url, { auth: { token }, transports: ['websocket', 'polling'] });
    } catch (_) {
      return undefined;
    }
    const onConnect = () => socket.emit('join');
    const onBlocked = (raw) => {
      let ev = raw;
      if (typeof raw === 'string') {
        try { ev = JSON.parse(raw); } catch { return; }
      }
      if (!ev) return;
      const key = blockKey(ev);
      if (seenRef.current.has(key)) return;
      seenRef.current.add(key);
      if (seenRef.current.size > 500) seenRef.current.clear();
      if (!suppressRef.current) {
        const reason = ev.reason || ev.detail || ev.code || 'risk limit reached';
        toast.show({ id: `risk:${key}`, tone: 'warn', title: 'Order blocked', body: reason });
      }
      setBlocks((n) => n + 1);
      refresh();
    };
    socket.on('connect', onConnect);
    socket.on('risk:blocked', onBlocked);
    setSocket(socket);
    return () => {
      socket.off('connect', onConnect);
      socket.off('risk:blocked', onBlocked);
      socket.disconnect();
      setSocket(null);
    };
  }, [enabled, refresh]);

  return { ...risk, refresh, socket, blocks, polling: Boolean(enabled && hasToken) };
}
